import EventEmitter from "eventemitter3";
import {io, Socket} from "socket.io-client";
import {v4 as uuid} from "uuid"
import {
    DescriptionEvent,
    P2PConnection,
    P2PConnectionDirection
} from "./connection";
import log from "loglevel";
import {formatObj, getLogger} from "./logging";
import {
    CallP2PMessage, DataChannelMessage,
    DescriptionP2PMessage,
    IceCandidateP2PMessage, InP2PMessage, P2PMessage
} from "./messages";
import * as version from "./version";

export interface P2PClientOptions {
    signalingUrl?: string
    token?: string
    configuration?: RTCConfiguration,
    logLevel?: log.LogLevelDesc
}

const defaultOptions = (): P2PClientOptions => {
    return {
        signalingUrl: window.location.origin,
        token: '',
        configuration: {},
        logLevel: log.levels.INFO
    }
}

export class P2PClient extends EventEmitter {
    private readonly _options: P2PClientOptions
    private readonly _logger: log.Logger
    private _socket: Socket
    private _outMediaStreams: { [key: string]: MediaStream } = {}
    private _sendConnections: { [key: string]: P2PConnection } = {}
    private _recvConnections: { [key: string]: P2PConnection } = {}
    private _tokens: { [key: string]: string } = {}

    constructor(options?: P2PClientOptions) {
        super();
        this._options = Object.assign({}, defaultOptions(), options)

        this._logger = getLogger('P2PClient', this._options.logLevel)

        this._logger.info(formatObj({
            version: version.number,
            commit: version.commit
        }))

        this._socket = io(
            new URL('p2p', this._options.signalingUrl).href,
            {
                transports: ["websocket"],
                auth: {
                    token: options?.token
                }
            }
        )

        this._socket.on('connect_error', (err) => {
            this._logger.error(formatObj({error: err}))
            this.emit('error', err)
        })

        this._socket.on('connect', () => {
            this.emit('ready')
        })

        this._socket.on('call', (message: CallP2PMessage & InP2PMessage) => {
            const connection = this._createSendConnection(message.connectionId, message.fromClientId)
            this.emit('connection', connection)
        })

        this._socket.on('description', async (message: DescriptionP2PMessage & InP2PMessage) => {
            let connection = null
            switch (message.description.type) {
                case 'offer':
                    connection = this._getConnection(message.fromClientId, P2PConnectionDirection.recv, message.connectionId)
                    break
                case 'answer':
                    connection = this._getConnection(message.fromClientId, P2PConnectionDirection.send, message.connectionId)
                    break
            }
            await connection?.handleDescription(
                new RTCSessionDescription(message.description),
                message.streams
            )
        })

        this._socket.on('iceCandidate', async (message: IceCandidateP2PMessage & InP2PMessage) => {
            for (const direction of [P2PConnectionDirection.recv, P2PConnectionDirection.send]) {
                const connection = this._getConnection(message.fromClientId, direction, message.connectionId)
                await connection?.addIceCandidate(new RTCIceCandidate(message.candidate))
            }
        })
    }

    public call(remoteClientId: string, token: string): P2PConnection {
        this._tokens[remoteClientId] = token
        return this._createRecvConnection(remoteClientId)
    }

    public addStream(name: string, mediaStream: MediaStream): void {
        this._outMediaStreams[name] = mediaStream
        Object.values(this._sendConnections)
            .forEach((connection) => {
                connection.addStream(name, mediaStream)
            })
    }

    public removeStream(name: string): void {
        delete this._outMediaStreams[name]
        Object.values(this._sendConnections)
            .forEach((connection) => {
                connection.removeStream(name)
            })
    }

    public sendMessage(remoteClientId: string, data: DataChannelMessage): void {
        const connection = this._sendConnections[remoteClientId]
        connection?.sendMessage(data)
    }

    public sendBroadcastMessage(data: DataChannelMessage): void {
        Object.keys(this._sendConnections)
            .forEach(remoteClientId => this.sendMessage(remoteClientId, data))
    }

    public stop(): void {
        this._socket.close();
        [
            ...Object.values(this._recvConnections),
            ...Object.values(this._sendConnections)
        ].forEach((connection) => connection.stop())
    }

    private _getConnection(remoteClientId: string, direction: P2PConnectionDirection, connectionId: string | undefined): P2PConnection | null {
        let connection = null
        switch (direction) {
            case P2PConnectionDirection.recv:
                connection = this._recvConnections[remoteClientId]
                break
            case P2PConnectionDirection.send:
                connection = this._sendConnections[remoteClientId]
                break
        }
        if (connection && connection.connectionId === connectionId)
            return connection
        return null
    }

    private _createConnection(connectionId: string, remoteClientId: string, direction: P2PConnectionDirection): P2PConnection {
        const connection = new P2PConnection(
            connectionId,
            remoteClientId,
            direction,
            this._logger,
            this._options.configuration,
            this._outMediaStreams
        )

        connection.on('call', () => {
            this._call(connectionId, remoteClientId)
        })

        connection.on('iceCandidate', ({candidate}: RTCPeerConnectionIceEvent) => {
            this._sendIceCandidate(connectionId, remoteClientId, candidate)
        })

        connection.on('description', ({description, streams}: DescriptionEvent) => {
            this._sendDescription(connectionId, remoteClientId, description, streams)
        })

        return connection
    }

    private _createSendConnection(connectionId: string, remoteClientId: string): P2PConnection {
        this._sendConnections[remoteClientId]?.stop()
        const connection = this._createConnection(connectionId, remoteClientId, P2PConnectionDirection.send)
        this._sendConnections[remoteClientId] = connection

        connection.on('message', (data: DataChannelMessage) => {
                this.emit(
                    'message',
                    {remoteClientId, data}
                )
            }
        )

        return connection
    }

    private _createRecvConnection(remoteClientId: string): P2PConnection {
        this._recvConnections[remoteClientId]?.stop()

        const connectionId = uuid()
        const connection = this._createConnection(
            connectionId, remoteClientId, P2PConnectionDirection.recv
        )
        this._recvConnections[remoteClientId] = connection
        return connection
    }

    private _sendToSignaling(eventName: string, data: P2PMessage): void {
        this._logger.info('SEND', formatObj({method: eventName, clientId: data.toClientId}), data)
        this._socket.emit(
            eventName,
            Object.assign({}, data, {'senderId': this._socket.id})
        )
    }

    private _call(connectionId: string, remoteClientId: string): void {
        this._sendToSignaling(
            'call',
            {
                toClientId: remoteClientId,
                token: this._tokens[remoteClientId],
                connectionId
            }
        )
    }

    private _sendIceCandidate(connectionId: string, remoteClientId: string, candidate: RTCIceCandidate | null): void {
        if (!candidate) return
        this._sendToSignaling('iceCandidate', {
            toClientId: remoteClientId,
            candidate: candidate.toJSON(),
            connectionId
        })
    }

    private _sendDescription(connectionId: string, remoteClientId: string, description: RTCSessionDescriptionInit, streams: { [key: string]: string }): void {
        this._sendToSignaling('description', {
            toClientId: remoteClientId,
            description: description,
            streams: streams,
            type: description.type,  // Backward compatibility
            connectionId
        })
    }
}
